// src/store/cartStore.ts
import { create } from "zustand";
import { useUserStore } from "./userStore";
import api from "../api";

/* ----------------------------- Types / DTOs ----------------------------- */

export type CartItemDto = {
  cartItemId: number;
  partId: number;
  figId?: number | null;
  itemNo?: string | null;
  quantity: number;
  partNumber?: string | null;
  description?: string | null;
  uom?: string | null;
  price?: number | null;
};

export type CartDto = {
  cartId: number;
  vendorId?: number | null;
  purchaserEmail?: string | null;
  createdOn?: string | null;
  status?: "open" | "cancelled" | "placed";
  items: CartItemDto[];
};

type CartSummary = { cartId: number | null; totalQuantity: number; totalAmount: number };

// Lightweight history row (no items)
export type CartLiteDto = {
  cartId: number;
  vendorId?: number | null;
  purchaserEmail?: string | null;
  createdOn?: string | null;
  status?: "open" | "cancelled" | "placed";
  totalQuantity?: number | null;
  totalAmount?: number | null;
};

type AddCartItemRequest = { partId: number; figId?: number | null; itemNo?: string | null; quantity: number };
type UpdateCartItemQuantityRequest = { cartItemId: number; quantity: number };

type ApiResponse<T = any> = { statusCode: number; message?: string; data?: T };

/* -------------------------------- Helpers ------------------------------- */

function sumQty(cart: CartDto | null): number {
  if (!cart?.items) return 0;
  return cart.items.reduce((s, i) => s + (i.quantity ?? 0), 0);
}

function sumAmount(cart: CartDto | null): number {
  if (!cart?.items) return 0;
  return cart.items.reduce((s, i) => s + (i.quantity ?? 0) * Number(i.price ?? 0), 0);
}

/**
 * Resolve OEM string used in routes:
 * 1) legacy currentOEM.oemName
 * 2) derive from user.oemId => "Oem{n}"
 * 3) fallback VITE_DEFAULT_OEM or "Oem1"
 */
function resolveOem(): string {
  const s: any = useUserStore.getState();
  const legacy = s.currentOEM?.oemName as string | undefined;
  if (legacy && String(legacy).trim()) return legacy;

  const u = s.user as { oemId?: number } | null;
  if (u?.oemId) return `Oem${u.oemId}`;

  const envDefault = (import.meta as any)?.env?.VITE_DEFAULT_OEM as string | undefined;
  return envDefault && envDefault.trim() ? envDefault : "Oem1";
}

// axios JSON helper; handles 204
async function httpJson<T>(fn: () => Promise<{ data: T; status: number }>): Promise<T> {
  const res = await fn();
  if (res.status === 204 && (res as any).data == null) {
    return { statusCode: 204 } as unknown as T;
  }
  return res.data;
}

/* --------------------------------- Store -------------------------------- */

interface CartStore {
  // editable/open cart detail (vendor flow)
  cart: CartDto | null;
  cartIds: number[];
  loading: boolean;
  error: string | null;
  itemsCount: number;
  linesCount: number;

  // open cart badge/summary (vendor only)
  openCartId: number | null;
  totalAmount: number;

  // history list (lightweight, no items)
  history: CartLiteDto[];
  historyLoading: boolean;
  historyError: string | null;

  // read-only details view (for placed/cancelled/open), separate from "cart"
  viewCart: CartDto | null;
  viewLoading: boolean;
  viewError: string | null;

  // actions
  initOnLogin: () => Promise<void>;
  loadSummary: () => Promise<void>;
  loadHistory: () => Promise<void>;
  setCartIds: (ids: number[]) => void;
  addCartId: (id: number) => void;
  loadCartById: (cartId: number) => Promise<void>;
  refreshOpenCart: () => Promise<void>;

  addItem: (o: AddCartItemRequest) => Promise<void>;
  updateQty: (cartItemId: number, quantity: number) => Promise<void>;
  removeItem: (cartItemId: number) => Promise<void>;
  cancelOpenCart: () => Promise<void>;
  placeOpenCart: (payload?: any) => Promise<void>;

  loadCartDetails: (cartId: number) => Promise<void>; // read-only
  clearView: () => void;

  clear: () => void;
}

export const useCartStore = create<CartStore>((set, get) => ({
  cart: null,
  cartIds: [],
  loading: false,
  error: null,
  itemsCount: 0,
  linesCount: 0,

  openCartId: null,
  totalAmount: 0,

  history: [],
  historyLoading: false,
  historyError: null,

  viewCart: null,
  viewLoading: false,
  viewError: null,

  setCartIds: (ids) => {
    set({ cartIds: Array.from(new Set(ids)) });
  },

  addCartId: (id) => {
    const next = Array.from(new Set([...(get().cartIds ?? []), id]));
    set({ cartIds: next });
  },

  /* --------------------------- Vendor-only summary --------------------------- */
  async loadSummary() {
    const { user } = useUserStore.getState();
    const role = String(user?.role ?? "").trim().toUpperCase();
    const isVendor = role.includes("VENDOR");

    if (!isVendor) {
      set({ openCartId: null, itemsCount: 0, totalAmount: 0 });
      return;
    }

    const vendorId = Number(user?.vendorId ?? 0);
    const oem = resolveOem();

    try {
      const resp = await httpJson<ApiResponse<CartSummary>>(() =>
        api.get<ApiResponse<CartSummary>>(`/Carts/summary/${oem}/${vendorId}`)
      );

      if (resp.statusCode === 200 && resp.data) {
        const { cartId, totalQuantity, totalAmount } = resp.data;
        set({
          openCartId: cartId ?? null,
          itemsCount: totalQuantity ?? 0,
          totalAmount: totalAmount ?? 0,
        });
      } else {
        set({ openCartId: null, itemsCount: 0, totalAmount: 0 });
      }
    } catch {
      set({ openCartId: null, itemsCount: 0, totalAmount: 0 });
    }
  },

  /* ------------------------------ History list ------------------------------ */
  async loadHistory() {
    const { user } = useUserStore.getState();
    const role = String(user?.role ?? "").trim().toUpperCase();
    const isVendor = role.includes("VENDOR");
    const vendorId = isVendor ? Number(user?.vendorId ?? 0) : 0;
    const oem = resolveOem();

    set({ historyLoading: true, historyError: null });
    try {
      const resp = await httpJson<ApiResponse<CartLiteDto[]>>(() =>
        api.get<ApiResponse<CartLiteDto[]>>(`/Carts/history/${oem}/${vendorId}`)
      );

      if (resp.statusCode === 200 && Array.isArray(resp.data)) {
        set({ history: resp.data, historyLoading: false });
      } else {
        set({ history: [], historyLoading: false });
      }
    } catch (e: any) {
      set({ history: [], historyLoading: false, historyError: e?.message ?? "Failed to load history" });
    }
  },

  /* ------------------------------ Init on login ----------------------------- */
  async initOnLogin() {
    const { accessToken, user } = useUserStore.getState();
    if (!accessToken) {
      set({
        cart: null,
        itemsCount: 0,
        linesCount: 0,
        openCartId: null,
        totalAmount: 0,
        error: null,
        history: [],
        historyLoading: false,
        historyError: null,
        viewCart: null,
        viewLoading: false,
        viewError: null,
      });
      return;
    }

    await get().loadHistory();

    const role = String(user?.role ?? "").trim().toUpperCase();
    if (role.includes("VENDOR")) {
      await get().loadSummary();
    } else {
      set({ openCartId: null, itemsCount: 0, totalAmount: 0 });
    }
  },

  /* --------------------------- Load open cart detail ------------------------- */
  async loadCartById(cartId) {
    const oem = resolveOem();
    set({ loading: true, error: null });
    try {
      const resp = await httpJson<ApiResponse<CartDto>>(() =>
        api.get<ApiResponse<CartDto>>(`/Carts/${oem}/${cartId}`)
      );

      if (resp.statusCode === 200 && resp.data) {
        const cart = resp.data;
        set({
          cart,
          itemsCount: sumQty(cart),
          linesCount: cart.items?.length ?? 0,
          loading: false,
          totalAmount: sumAmount(cart),
        });
        get().addCartId(cart.cartId);
      } else {
        set({ loading: false, error: resp.message ?? "Cart not found" });
      }
    } catch (e: any) {
      set({ loading: false, error: e?.message ?? "Failed to load cart" });
    }
  },

  async refreshOpenCart() {
    await get().initOnLogin();
    const id = get().openCartId;
    if (id) await get().loadCartById(id);
  },

  /* ------------------------------- Mutations -------------------------------- */
  async addItem(req) {
    const { user } = useUserStore.getState();
    const role = String(user?.role ?? "").trim().toUpperCase();
    if (!role.includes("VENDOR")) throw new Error("Only vendors can add items to a cart");

    const vendorId = Number(user?.vendorId ?? 0);
    const oem = resolveOem();
    if (!vendorId || !oem) throw new Error("Missing vendor/oem");

    const resp = await httpJson<ApiResponse<CartDto>>(() =>
      api.post<ApiResponse<CartDto>>(`/Carts/add/${oem}/${vendorId}`, req)
    );

    if (resp.statusCode === 200 && resp.data) {
      const cart = resp.data;
      set({
        cart,
        itemsCount: sumQty(cart),
        linesCount: cart.items?.length ?? 0,
        openCartId: cart.cartId,
        totalAmount: sumAmount(cart),
      });
      get().addCartId(cart.cartId);
    } else {
      throw new Error(resp.message ?? "Add item failed");
    }
  },

  async updateQty(cartItemId, quantity) {
    const { user } = useUserStore.getState();
    const role = String(user?.role ?? "").trim().toUpperCase();
    if (!role.includes("VENDOR")) throw new Error("Only vendors can update cart items");

    const vendorId = Number(user?.vendorId ?? 0);
    const oem = resolveOem();
    if (!vendorId || !oem) throw new Error("Missing vendor/oem");

    const body: UpdateCartItemQuantityRequest = { cartItemId, quantity };
    const resp = await httpJson<ApiResponse<CartDto>>(() =>
      api.patch<ApiResponse<CartDto>>(`/Carts/qty/${oem}/${vendorId}`, body)
    );

    if (resp.statusCode === 200 && resp.data) {
      const cart = resp.data;
      set({
        cart,
        itemsCount: sumQty(cart),
        linesCount: cart.items?.length ?? 0,
        totalAmount: sumAmount(cart),
      });
    } else {
      throw new Error(resp.message ?? "Update failed");
    }
  },

  async removeItem(cartItemId) {
    const { user } = useUserStore.getState();
    const role = String(user?.role ?? "").trim().toUpperCase();
    if (!role.includes("VENDOR")) throw new Error("Only vendors can remove cart items");

    const vendorId = Number(user?.vendorId ?? 0);
    const oem = resolveOem();
    if (!vendorId || !oem) throw new Error("Missing vendor/oem");

    const resp = await httpJson<ApiResponse<CartDto>>(() =>
      api.delete<ApiResponse<CartDto>>(`/Carts/item/${oem}/${vendorId}/${cartItemId}`)
    );

    if (resp.statusCode === 200 && resp.data) {
      const cart = resp.data;
      set({
        cart,
        itemsCount: sumQty(cart),
        linesCount: cart.items?.length ?? 0,
        totalAmount: sumAmount(cart),
      });
    } else {
      throw new Error(resp.message ?? "Remove failed");
    }
  },

  async cancelOpenCart() {
    const { user } = useUserStore.getState();
    const role = String(user?.role ?? "").trim().toUpperCase();
    if (!role.includes("VENDOR")) throw new Error("Only vendors can cancel carts");

    const vendorId = Number(user?.vendorId ?? 0);
    const oem = resolveOem();
    const cartId = get().cart?.cartId ?? get().openCartId;
    if (!vendorId || !oem || !cartId) throw new Error("Missing vendor/oem/cartId");

    const resp = await httpJson<ApiResponse>(() =>
      api.post<ApiResponse>(`/Carts/cancel/${oem}/${vendorId}/${cartId}`, {})
    );

    if (resp.statusCode === 200) {
      set({ cart: null, itemsCount: 0, linesCount: 0, openCartId: null, totalAmount: 0 });
      await get().loadHistory();
    } else {
      throw new Error(resp.message ?? "Cancel failed");
    }
  },

  async placeOpenCart(payload) {
    const { user } = useUserStore.getState();
    const role = String(user?.role ?? "").trim().toUpperCase();
    if (!role.includes("VENDOR")) throw new Error("Only vendors can place orders");

    const vendorId = Number(user?.vendorId ?? 0);
    const oem = resolveOem();
    const cartId = get().cart?.cartId ?? get().openCartId;
    if (!vendorId || !oem || !cartId) throw new Error("Missing vendor/oem/cartId");

    const body = {
      purchaserEmail: JSON.stringify(payload ?? {}),
    };
    const resp = await httpJson<ApiResponse>(() =>
      api.post<ApiResponse>(`/Carts/place/${oem}/${vendorId}/${cartId}`, body)
    );

    if (resp.statusCode === 200) {
      set({ cart: null, itemsCount: 0, linesCount: 0, openCartId: null, totalAmount: 0 });
      await get().loadHistory();
    } else {
      throw new Error(resp.message ?? "Place failed");
    }
  },

  /* ----------------------------- Read-only view ----------------------------- */
  async loadCartDetails(cartId) {
    const oem = resolveOem();
    set({ viewLoading: true, viewError: null });
    try {
      const resp = await httpJson<ApiResponse<CartDto>>(() =>
        api.get<ApiResponse<CartDto>>(`/Carts/${oem}/${cartId}`)
      );
      if (resp.statusCode === 200 && resp.data) {
        set({ viewCart: resp.data, viewLoading: false });
      } else {
        set({ viewCart: null, viewLoading: false, viewError: resp.message ?? "Cart not found" });
      }
    } catch (e: any) {
      set({ viewCart: null, viewLoading: false, viewError: e?.message ?? "Failed to load cart details" });
    }
  },

  clearView: () => set({ viewCart: null, viewLoading: false, viewError: null }),

  /* --------------------------------- Clear ---------------------------------- */
  clear: () =>
    set({
      cart: null,
      itemsCount: 0,
      linesCount: 0,
      cartIds: [],
      error: null,
      openCartId: null,
      totalAmount: 0,
      history: [],
      historyLoading: false,
      historyError: null,
      viewCart: null,
      viewLoading: false,
      viewError: null,
    }),
}));
